import { AxiosError } from "axios";
import {
  BadRequestError,
  NetworkUnknownError,
  NotFoundError,
  ServerError,
  UnauthorizedUser,
  UnhandledApiError,
  UnproccessableContentError,
} from "../Constant";

/**
 * Handles API errors by mapping HTTP status codes to specific error-handling logic.
 * If a specific handler is not defined for a status code, it logs an "unhandled API error."
 * @param error - The AxiosError object representing the error.
 * @throws The same error to allow the caller to handle it further.
 */
export const handleApiError = (error: AxiosError): never => {
  // A map of HTTP status codes to corresponding error handlers.
  const statusHandler: Record<number, (data: any) => void> = {
    400: (data) => console.error(BadRequestError, data), // Handles 400 Bad Request errors.
    401: (data) => console.error(UnauthorizedUser, data), // Handles 401 Unauthorized errors.
    403: (data) => console.error("session timeout", data),
    404: (data) => console.error(NotFoundError, data), // Handles 404 Not Found errors.
    422: (data) => console.error(UnproccessableContentError, data),
    500: (data) => console.error(ServerError, data), // Handles 500 Internal Server Error.
  };

  // Check if the error response exists (i.e., the server returned a response).
  if (error.response) {
    const { status, data } = error.response; // Extract the status code and data from the response.

    // Find the handler for the specific status code or use a default handler for unhandled errors.
    const handler =
      statusHandler[status] ||
      ((data) => console.error(UnhandledApiError, data));

    handler(data); // Execute the appropriate error handler.
  } else {
    // If no response is available, it might be a network error or an unknown error.
    console.error(NetworkUnknownError, error.message);
  }

  // Rethrow the error to allow further handling by the caller.
  throw error;
};
